import { useMutation } from "react-query";
import axios from "axios";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * Simple hook to upload file to GCS via signed URL
 * That's it. Nothing else.
 *
 * @param {Object} options
 * @param {File} options.file - The file to upload
 * @param {string} options.autoInjectRowIdKey - Key for auto-injected row IDs
 * @param {string} [options.sheetName] - Which sheet to parse. Only set for a
 *                                        multi-sheet workbook the user picked from;
 *                                        omitted means "first sheet" (CSV, single-sheet).
 * @param {string} [options.contentType] - Optional content type for reimport mode.
 *                                          If provided, backend will return stored outline
 *                                          instead of auto-generating config.
 */
export const useUploadDirectDataImportFileToGCS = () => {
  return useMutation({
    mutationFn: async ({ file, autoInjectRowIdKey, sheetName, contentType }) => {
      const mediaConfigs = {
        folderName: SM_UIContent.SM_DirectDataImport.folderName,
        allowedFormats: ["xlsx", "xls", "csv"],
      };
      // Step 1: Get signed URL (only metadata sent to backend)
      const signedUrlResponse = await axios.post(
        `/api/media/createSignedUrl?configs=${encodeURIComponent(
          JSON.stringify(mediaConfigs)
        )}`,
        {
          files: [
            {
              name: file.name,
              type: file.type,
              size: file.size,
            },
          ],
        }
      );

      const { signedUrl, fileNameInGcs, status } = signedUrlResponse.data[0];

      if (status === "error" || !signedUrl) {
        throw new Error("Failed to get signed URL");
      }

      // Step 2: Upload file directly to GCS
      const newAxiosInstance = axios.create();
      delete newAxiosInstance.defaults.withCredentials;

      await newAxiosInstance.put(signedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // Step 3: Process the uploaded file
      // If contentType is provided (reimport mode), backend will return stored outline
      const processResponse = await axios.post(
        "/api/content/processExcelForDirectDataImport",
        {
          fileNameInGcs,
          autoInjectRowIdKey,
          ...(sheetName && { sheetName }), // Only include if a sheet was picked
          ...(contentType && { contentType }), // Only include if provided
        }
      );

      return processResponse.data; // Returns: { success, config, metadata, fileNameInGcs }
    }
  });
};
